import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import queryString from 'query-string';
import { addAction } from '@utils/logger';
import timezone from 'dayjs/plugin/timezone';
import { joyAllEventTypes } from './HeroSection.utils';
import { useFindACoupleGQLQuery } from './useFindACoupleGQLQuery';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { EventData, EventType, AllEventTypes } from './HeroSection.types';
import { Integrations } from '@static/js/types';
import { useTranslation } from 'react-i18next';

const analytics = require('/static/js/joy');

export const useHeroSectionController = (eventType: AllEventTypes) => {
  const [inputValue, setInputValue] = useState<string | string[]>('');
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState('');
  const [searchResult, setSearchResult] = useState<EventData[] | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shouldShowAll, setShouldShowAll] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<AllEventTypes>('');
  const { fetchData } = useFindACoupleGQLQuery();

  const { t } = useTranslation('findAnEvent');
  const { emptyInput, fetchMsg, eventFoundMsg, eventsFoundMsg, moreEventsFoundMsg, eventsNotFoundMsg } = t('findACouple.heroSection');

  const handleFindApiRequest = useCallback(
    (searchTerm: string, _eventType: AllEventTypes | '', eventTypeFilters: EventType[]) => {
      const filteredBy = _eventType === '' ? 'All Events' : _eventType;
      analytics.segment.sendTelemetryEvent(
        'newmarketing.findACoupleSearchClick',
        {
          label: searchTerm,
          extraInfo: {
            filteredBy,
          },
        },
        undefined,
        { integrations: { [Integrations.Amplitude]: true } }
      );

      addAction('FindApiRequest', {
        type: filteredBy,
        searchTerm,
      });
      // Fetch search results
      setLoading(true);
      fetchData(searchTerm, eventTypeFilters)
        .then((response) => {
          setSearchText(searchTerm);
          setLoading(true);
          let results = response.getEventsBySearch.results;
          let totalCount = response.getEventsBySearch.totalCount;
          return { results, totalCount, status: 200, error: undefined };
        })
        .catch((error) => {
          return { results: [], totalCount: 0, status: 500, error: error?.message };
        })
        .then(({ results, totalCount, status, error }) => {
          // Perform actions after success or failure
          results = results
            ? results.filter(
                (result) =>
                  result.event.info.ownerFullName && result.event.info.ownerFullName !== null && result.event.info.ownerFullName !== 'null'
              )
            : [];
          if (results.length === 0) {
            setShowSuggestions(true);
          }
          setShouldShowAll(false);
          setSearchResult(results);
          setLoading(false);

          analytics.segment.sendTelemetryEvent(
            'newmarketing.findACoupleResultsFound',
            {
              label: totalCount + 'Found',
              value: totalCount,
              searchTerm,
              numberOfResults: totalCount,
              extraInfo: {
                filteredBy,
              },
            },
            undefined,
            { integrations: { [Integrations.Amplitude]: true } }
          );
          addAction('FindApiResponse', {
            status,
            type: filteredBy,
            searchTerm,
            error,
          });
        });
    },
    [fetchData]
  );

  const handleSubmit = useCallback(() => {
    const eventTypeFilters = eventType ? [EventType[eventType as keyof typeof EventType]] : joyAllEventTypes;
    setSelectedEventType(eventType);
    setShowSuggestions(false);
    if (!inputValue) {
      setError(emptyInput);
      return;
    }
    handleFindApiRequest(inputValue.toString(), eventType, eventTypeFilters);
  }, [emptyInput, eventType, handleFindApiRequest, inputValue]);

  useEffect(() => {
    const eventTypeFilters = eventType ? [EventType[eventType as keyof typeof EventType]] : joyAllEventTypes;
    const name = queryString.parse(typeof window === 'undefined' ? '' : window.location.search).name || '';
    if (name && name.length > 0) {
      setInputValue(name);
      setSelectedEventType(eventType);
      setLoading(true);
      handleFindApiRequest(name.toString(), eventType, eventTypeFilters);
    }
  }, [eventType, handleFindApiRequest, inputValue]);

  /**
   * If fetched data with All Event Types
   *  - Filter result by eventType
   *  - Filter result by eventType on change eventType
   * If fetched data with selected event type then on change eventType, need to refetch data from API
   * */
  const filteredSearchResult = useMemo(() => {
    const filteredSearchResult = eventType ? searchResult?.filter((result) => result.event.info.eventType === eventType) : searchResult;
    if (
      ((filteredSearchResult?.length === 0 && selectedEventType !== '') || (eventType === '' && selectedEventType !== '')) &&
      eventType !== selectedEventType
    ) {
      handleSubmit();
    }
    return filteredSearchResult;
  }, [eventType, handleSubmit, searchResult, selectedEventType]);

  const displayedResults = useMemo(
    () =>
      filteredSearchResult && filteredSearchResult.length > 5 && !shouldShowAll ? filteredSearchResult.slice(0, 5) : filteredSearchResult,
    [filteredSearchResult, shouldShowAll]
  );

  dayjs.extend(utc);
  dayjs.extend(timezone);

  let message: string;

  switch (true) {
    case filteredSearchResult && filteredSearchResult.length <= 50 && filteredSearchResult.length === 1:
      message = eventFoundMsg;
      break;
    case filteredSearchResult && filteredSearchResult.length <= 50 && filteredSearchResult.length > 1:
      message = `${filteredSearchResult?.length} ${eventsFoundMsg}`;
      break;
    case filteredSearchResult && filteredSearchResult.length > 50:
      message = moreEventsFoundMsg;
      break;
    default:
      message = `${eventsNotFoundMsg} "${searchText}"`;
      break;
  }

  const handleChangeInput = (value: string) => {
    setError('');
    if (value.length === 0) {
      setSearchResult(null);
      setShowSuggestions(false);
      setInputValue(value);
    } else {
      setInputValue(value);
    }
  };

  let loadingMessage: string | null = fetchMsg;

  if (!loading) {
    loadingMessage = message;
  } else if (inputValue === '') {
    loadingMessage = '';
  }

  return {
    error,
    loading,
    shouldShowAll,
    loadingMessage,
    showSuggestions,
    displayedResults,
    filteredSearchResult,
    handleSubmit,
    setShouldShowAll,
    handleChangeInput,
  };
};
